'use client';

import { c as _c } from "react/compiler-runtime";
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { unique } from '@lokalise/universal-ts-utils/array/unique';
import type { Data } from '@repo/strapi-types';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { MarketoForm } from '@/app/[locale]/blog/_components/MarketoForm';
import AppLink from '@/components/elementary/AppLink';
import { Tag } from '@/components/elementary/Tag';
import { AppField } from '@/components/forms/AppField';
import { AppForm } from '@/components/forms/AppForm';
import { Close } from '@/components/icons/Close';
import { Search } from '@/components/icons/Search';
import { ChangelogItem } from '@/components/page-builder/global/changelog/ChangelogItem';
import Heading from '@/components/typography/Heading';
import { Paragraph } from '@/components/typography/Paragraph';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getAllCategories, MARKETO_FORM_IDS } from '@/lib/constants';
import { formatDate } from '@/lib/dates';
import { cn } from '@/lib/styles';
import { CHANGELOG_TEST_IDS } from '@/lib/test-ids';
import type { ServerRenderedEntry } from '@/types/helpers';
export const ChangelogFilters = ({
  entries,
  categories,
  sidebarInfoBanner
}: {
  readonly categories: Data.ContentType<'api::changelog-category.changelog-category'>[];
  readonly entries: ServerRenderedEntry<Data.ContentType<'api::change-log.change-log'>>[];
  readonly sidebarInfoBanner?: Data.Component<'global.sidebar-info-banner'> | null;
}) => {
  const t = useTranslations('ChangelogFilters');
  const form = useForm<z.infer<FormSchemaType>>({
    resolver: standardSchemaResolver(FilterFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onSubmit'
  });
  const selectedCategory = form.watch('category');
  const selectedDate = form.watch('date');
  const searchTerm = form.watch('search')?.toLowerCase() || '';
  const filteredDates = unique(entries.filter(entry => entry.changeDate !== null && entry.changeDate !== undefined).sort((a, b) => new Date(b.changeDate ?? 0).getTime() - new Date(a.changeDate ?? 0).getTime()).map(entry_0 => formatDate(entry_0.changeDate as string | Date, 'MMMM yyyy')));
  const changelogsFilteredByCategory = useMemo(() => {
    if (!selectedCategory || selectedCategory.id === 0) return entries;
    return entries.filter(entry_1 => entry_1?.categories?.some(category => category?.id === selectedCategory?.id));
  }, [selectedCategory, entries]);
  const filteredChangelogs = useMemo(() => {
    let result = changelogsFilteredByCategory;
    if (searchTerm) {
      result = result.filter(entry_2 => entry_2?.htmlContent?.toLowerCase().includes(searchTerm));
    }
    if (selectedDate && selectedDate !== 'all') {
      result = result.filter(entry_3 => {
        const formattedDate = formatDate(entry_3.changeDate as string | Date, 'MMMM yyyy');
        return formattedDate === selectedDate;
      });
    }
    return result;
  }, [changelogsFilteredByCategory, searchTerm, selectedDate]);
  const allCategories = getAllCategories(t('allCategories'));
  return <div className="flex lg:flex-row flex-col-reverse lg:gap-17" data-test-id={CHANGELOG_TEST_IDS.FILTERS}>
      <div className="flex flex-1 flex-col">
        {filteredChangelogs?.length < 1 ? <Paragraph className="lg:pt-14" data-test-id={CHANGELOG_TEST_IDS.NO_RESULTS}>
            {t('noResults')}
          </Paragraph> : filteredChangelogs.map(entryItem => <ChangelogItem key={entryItem.id} entryItem={entryItem} />)}
      </div>
      <div className="w-full lg:w-71.5 flex flex-col gap-6 lg:pt-14">
        <AppForm form={form} id={filterFormName} onSubmit={() => {}}>
          <AppField containerClassName="lg:flex hidden w-full" fieldClassName="w-full" name="search" placeholder={t('searchPlaceholder')} iconLeft={<Search />} data-test-id={CHANGELOG_TEST_IDS.SEARCH_INPUT} />
          {searchTerm && <GhostButton name="category" onClick={() => form.setValue('search', '')} className="absolute right-3 -translate-y-1/2 top-1/2" data-test-id={CHANGELOG_TEST_IDS.SEARCH_CLEAR}>
              <Close className="text-neutral-light-600 size-4" />
            </GhostButton>}
        </AppForm>

        {sidebarInfoBanner && <div className="@container bg-peach-200 w-full rounded-xl p-5">
            <Paragraph fontSize="headingSmall" className="mb-3 @sidebar-compact:mb-2">
              {sidebarInfoBanner.title}
            </Paragraph>
            <Paragraph fontSize="bodyRegularMedium" className="mb-4 @sidebar-compact:mb-5">
              {sidebarInfoBanner.description}
            </Paragraph>
            <AppLink link={sidebarInfoBanner.link} title={sidebarInfoBanner.link?.label ?? ''} variant="primary" size="medium" className="w-full">
              {sidebarInfoBanner.link?.label}
            </AppLink>
          </div>}

        <div className="h-px w-full bg-neutral-light-500" />
        <div className="flex flex-wrap gap-2.5 justify-center lg:justify-start">
          <GhostButton key={allCategories.id} name="category" onClick={() => form.setValue('category', allCategories)} data-test-id={CHANGELOG_TEST_IDS.CATEGORY_ALL}>
            <Tag name={allCategories?.name} active={selectedCategory?.id === allCategories.id} customColors={{
            backgroundColor: allCategories.backgroundColor?.color,
            borderColor: allCategories.borderColor?.color
          }} />
          </GhostButton>
          {categories?.map(category_0 => <GhostButton key={category_0.id} name="category" onClick={() => form.setValue('category', category_0)} data-test-id={CHANGELOG_TEST_IDS.CATEGORY}>
              <Tag name={category_0?.name} active={selectedCategory?.id === category_0.id} customColors={{
            backgroundColor: category_0.backgroundColor?.color,
            borderColor: category_0.borderColor?.color
          }} />
            </GhostButton>)}
        </div>
        <div className="h-px w-full bg-neutral-light-500 lg:flex hidden" />
        <ul className="flex-col gap-2 lg:flex hidden">
          <GhostButton name="date" className="flex justify-start" onClick={() => form.setValue('date', 'all')} data-test-id={CHANGELOG_TEST_IDS.FILTERS_ALL}>
            <li>
              <Paragraph fontSize="bodyRegularLarge" className={cn({
              'font-bold': selectedDate === 'all'
            })}>
                {t('allDates')}
              </Paragraph>
            </li>
          </GhostButton>
          {filteredDates.map(date => <GhostButton key={date} name="date" onClick={() => form.setValue('date', date)} data-test-id={CHANGELOG_TEST_IDS.FILTERS_DATE}>
              <li>
                <Paragraph fontSize="bodyRegularLarge" className={cn({
              'font-bold': selectedDate === date
            })}>
                  {date}
                </Paragraph>
              </li>
            </GhostButton>)}
        </ul>
        <div className="h-px w-full bg-neutral-light-500" />
        <div className="flex flex-col gap-3 w-full lg:max-w-71.5 bg-peach-100 p-5 rounded-xl lg:sticky lg:top-33">
          <Heading tag="h2" fontSize="headingSmall">
            {t('subscriptionTitle')}
          </Heading>
          <Paragraph fontSize="bodyRegularMedium">{t('subscriptionDescription')}</Paragraph>
          <MarketoForm id={MARKETO_FORM_IDS.NEWSLETTER_SUBSCRIPTION_LOKALOG} loadingSkeleton={<NewsletterSubscriptionSkeleton />} className="overflow-hidden" />
        </div>
      </div>
    </div>;
};
const FilterFormSchema = z.object({
  search: z.string().optional(),
  category: z.any().optional(),
  date: z.any().optional()
});
type FormSchemaType = typeof FilterFormSchema;
export const filterFormName = 'changelogFilter';
const GhostButton = t0 => {
  const $ = _c(9);
  const {
    children,
    name,
    onClick,
    className
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = () => {
      onClick();
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClick = t1;
  let t2;
  if ($[2] !== className) {
    t2 = cn("flex justify-start", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== handleClick || $[6] !== name || $[7] !== t2) {
    t3 = <Button name={name} variant="none" size="none" className={t2} onClick={handleClick}>{children}</Button>;
    $[4] = children;
    $[5] = handleClick;
    $[6] = name;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const NewsletterSubscriptionSkeleton = () => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-11.25 w-full mb-7.75" />;
    t1 = <Skeleton className="h-9 sm:h-5.75 lg:h-9 w-full mb-5.25 lg:mb-4" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="pt-5">{t0}{t1}<div className="flex flex-col gap-1.5 lg:gap-1.5 mb-3.75 lg:mb-2.5"><Skeleton className="h-2.75 lg:h-4 w-full" /><Skeleton className="h-2.75 lg:h-4 w-1/2" /><Skeleton className="h-2.75 lg:h-4 w-1/3" /></div><Skeleton className="h-11.25 w-full rounded-full" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};